import React, { useState, useEffect } from 'react';
import {
    getProposalById,
    updateProposal,
    markAsReadyForReview,
    submitMockProposal,
    getProposalStatus,
    getMockClientFeedback,
    deleteProposal
} from '../services/proposalService';
import { usePermissions } from '../hooks/usePermissions';
import { ArrowLeft, Edit, Eye, Plus, X, Upload, ExternalLink, MapPin, Clock, CheckCircle } from 'lucide-react';
import { FileUpload } from './FileUpload';

/**
 * Inline Proposal Details component for modal display
 * Simplified version of ProposalDetails without routing dependencies
 */
export function InlineProposalDetails({ proposal: initialProposal, onUpdate, onClose }) {
    const [proposal, setProposal] = useState(initialProposal);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [clientFeedback, setClientFeedback] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const { canAccessAdminPanel } = usePermissions();

    // Refresh proposal data when it changes
    useEffect(() => {
        setProposal(initialProposal);
        if (initialProposal?._id) {
            refreshProposalData(initialProposal._id);
        }
    }, [initialProposal?._id]);

    const refreshProposalData = async (id) => {
        try {
            const response = await getProposalById(id);
            if (response.success) {
                setProposal(response.data);
                if (response.data.status === 'submitted' && response.data.source !== 'upwork') {
                    loadSubmissionStatus(id);
                    loadClientFeedback(id);
                }
            }
        } catch (err) {
            // Silent fail for background refresh
        }
    };

    const loadSubmissionStatus = async (id) => {
        try {
            const response = await getProposalStatus(id);
            if (response.success) {
                setSubmissionStatus(response.data);
            }
        } catch {
            // Silent
        }
    };

    const loadClientFeedback = async (id) => {
        try {
            const response = await getMockClientFeedback(id);
            if (response.success) {
                setClientFeedback(response.data);
            }
        } catch {
            // Silent
        }
    };

    const handleMarkForReview = async () => {
        try {
            setActionLoading(true);
            const response = await markAsReadyForReview(proposal._id);
            if (response.success) {
                refreshProposalData(proposal._id);
                onUpdate?.();
            } else {
                setError(response.error || 'Failed to mark proposal for review');
            }
        } catch (err) {
            setError(err.message || 'Failed to mark proposal for review');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!window.confirm('Are you sure you want to submit this proposal to Upwork?')) {
            return;
        }
        try {
            setActionLoading(true);
            const response = await submitMockProposal(proposal._id);
            if (response.success) {
                refreshProposalData(proposal._id);
                onUpdate?.();
            } else {
                setError(response.error || 'Failed to submit proposal');
            }
        } catch (err) {
            setError(err.message || 'Failed to submit proposal');
        } finally {
            setActionLoading(false);
        }
    };

    const handleEdit = () => {
        setEditData({
            title: proposal.title,
            description: proposal.description,
            coverLetter: proposal.coverLetter,
            pricing: proposal.pricing,
            attachments: proposal.attachments || [],
            portfolioItems: proposal.portfolioItems || [],
            links: proposal.links || [],
            screeningAnswers: proposal.screeningAnswers || []
        });
        setIsEditing(true);
    };

    const handleSaveEdit = async () => {
        try {
            setActionLoading(true);
            const response = await updateProposal(proposal._id, editData);
            if (response.success) {
                setProposal(response.data);
                setIsEditing(false);
                setEditData({});
                onUpdate?.();
            } else {
                setError(response.error || 'Failed to update proposal');
            }
        } catch (err) {
            setError(err.message || 'Failed to update proposal');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditData({});
    };

    const handleEditChange = (field, value) => {
        setEditData(prev => ({ ...prev, [field]: value }));
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this proposal? This action cannot be undone.')) {
            return;
        }
        try {
            setActionLoading(true);
            const response = await deleteProposal(proposal._id);
            if (response.success) {
                onClose();
                onUpdate?.();
            } else {
                setError(response.error || 'Failed to delete proposal');
            }
        } catch (err) {
            setError('Failed to delete proposal: ' + err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
            ready_for_review: { color: 'bg-yellow-100 text-yellow-800', label: 'Ready for Review' },
            under_review: { color: 'bg-primary-100 text-primary-800', label: 'Under Review' },
            approved: { color: 'bg-success-100 text-success-800', label: 'Approved' },
            rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
            submitted: { color: 'bg-purple-100 text-purple-800', label: 'Submitted' },
            submission_failed: { color: 'bg-red-100 text-red-800', label: 'Submission Failed' }
        };
        const config = statusConfig[status] || statusConfig.draft;
        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
                {config.label}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const jobTitle = proposal.jobPostingId?.title || proposal.title || 'Untitled Job';

    return (
        <div className="bg-white">
            {/* Header Actions */}
            <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {proposal.source === 'upwork' && (
                            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                                Synced from Upwork
                            </span>
                        )}
                        {getStatusBadge(proposal.status)}
                    </div>
                    <div className="flex items-center gap-2">
                        {proposal.source !== 'upwork' && (
                            <>
                                {(proposal.status === 'draft' || proposal.status === 'rejected' || proposal.status === 'changes_requested') && (
                                    <button
                                        onClick={handleEdit}
                                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors text-sm"
                                    >
                                        <Edit className="h-4 w-4" />
                                        Edit
                                    </button>
                                )}
                                {proposal.status === 'draft' && (
                                    <button
                                        onClick={handleMarkForReview}
                                        disabled={actionLoading}
                                        className="flex items-center gap-2 bg-accent-600 text-white px-4 py-2 rounded-md hover:bg-accent-700 transition-colors disabled:opacity-50 text-sm"
                                    >
                                        <Eye className="h-4 w-4" />
                                        {actionLoading ? 'Marking...' : 'Mark for Review'}
                                    </button>
                                )}
                                {proposal.status === 'approved' && (
                                    <button
                                        onClick={handleSubmit}
                                        disabled={actionLoading}
                                        className="flex items-center gap-2 bg-success-600 text-white px-4 py-2 rounded-md hover:bg-success-700 transition-colors disabled:opacity-50 text-sm"
                                    >
                                        <Upload className="h-4 w-4" />
                                        {actionLoading ? 'Submitting...' : 'Submit'}
                                    </button>
                                )}
                                {(proposal.status === 'draft' || proposal.status === 'rejected' || proposal.status === 'changes_requested' || proposal.status === 'approved') && (
                                    <button
                                        onClick={handleDelete}
                                        disabled={actionLoading}
                                        className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 text-sm"
                                    >
                                        <X className="h-4 w-4" />
                                        Delete
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
                {error && (
                    <div className="mt-3 bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm">
                        {error}
                    </div>
                )}
            </div>

            {/* Main Content */}
            <div className="p-6">
                {/* Edit Form */}
                {isEditing && proposal.source !== 'upwork' && (
                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-6">
                        <h3 className="text-lg font-semibold text-primary-900 mb-4">Edit Proposal</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={editData.title || ''}
                                    onChange={(e) => handleEditChange('title', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter</label>
                                <textarea
                                    value={editData.coverLetter || ''}
                                    onChange={(e) => handleEditChange('coverLetter', e.target.value)}
                                    rows={6}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Pricing Type</label>
                                    <select
                                        value={editData.pricing?.type || ''}
                                        onChange={(e) => handleEditChange('pricing', { ...editData.pricing, type: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="fixed">Fixed Price</option>
                                        <option value="hourly">Hourly</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                    <input
                                        type="number"
                                        value={editData.pricing?.amount || ''}
                                        onChange={(e) => handleEditChange('pricing', { ...editData.pricing, amount: parseFloat(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleSaveEdit}
                                    disabled={actionLoading}
                                    className="bg-success-600 text-white px-4 py-2 rounded-md hover:bg-success-700 disabled:opacity-50"
                                >
                                    {actionLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                    onClick={handleCancelEdit}
                                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Two Column Layout */}
                <div className="flex gap-6">
                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                        {/* Job Details */}
                        <div className="border border-gray-200 rounded-lg p-5 mb-6">
                            <h3 className="font-semibold text-gray-900 text-lg mb-1">{jobTitle}</h3>
                            <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                                <span>Created {formatDate(proposal.createdAt)}</span>
                                {proposal.upworkSyncedAt && (
                                    <span>• Synced {formatDate(proposal.upworkSyncedAt)}</span>
                                )}
                            </div>
                            {proposal.metadata?.jobDescription && (
                                <div className="mt-4">
                                    <p className="text-gray-700 text-sm whitespace-pre-wrap">{proposal.metadata.jobDescription}</p>
                                </div>
                            )}
                        </div>

                        {/* Proposed Terms */}
                        <div className="border border-gray-200 rounded-lg p-5 mb-6">
                            <h3 className="font-semibold text-gray-900 mb-4">Your proposed terms</h3>
                            <div className="space-y-4">
                                <div>
                                    <div className="text-gray-500 text-sm">
                                        {proposal.pricing?.type === 'hourly' ? 'Hourly rate' : 'Fixed price'}
                                    </div>
                                    <div className="text-gray-900 font-medium mt-1">
                                        ${proposal.pricing?.amount?.toLocaleString()}{proposal.pricing?.type === 'hourly' ? '/hr' : ''}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Cover Letter */}
                        <div className="border border-gray-200 rounded-lg p-5 mb-6">
                            <h3 className="font-semibold text-gray-900 mb-3">Cover letter</h3>
                            <p className="text-gray-700 text-sm whitespace-pre-wrap">{proposal.coverLetter}</p>
                        </div>

                        {/* Attachments */}
                        {proposal.attachments && proposal.attachments.length > 0 && (
                            <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                <h3 className="font-semibold text-gray-900 mb-3">Attachments</h3>
                                <div className="space-y-2">
                                    {proposal.attachments.map((attachment, index) => (
                                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <span className="text-2xl">📎</span>
                                            <div className="flex-1">
                                                <div className="font-medium text-sm">{attachment.name}</div>
                                                <div className="text-xs text-gray-500">
                                                    {attachment.mimeType || attachment.type}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Screening Questions */}
                        {proposal.screeningAnswers && proposal.screeningAnswers.length > 0 && (
                            <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                <h3 className="font-semibold text-gray-900 mb-3">Screening Questions</h3>
                                <div className="space-y-4">
                                    {proposal.screeningAnswers.map((qa, index) => (
                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                            <div className="font-medium text-gray-900 mb-2">{qa.question}</div>
                                            <div className="text-gray-700">{qa.answer}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Submission Status */}
                        {submissionStatus && proposal.source !== 'upwork' && (
                            <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                <h3 className="font-semibold text-gray-900 mb-3">Submission Status</h3>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div><strong>Status:</strong> {submissionStatus.upworkStatus}</div>
                                    <div><strong>Submitted:</strong> {formatDate(submissionStatus.submittedAt)}</div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar - Client Info */}
                    {proposal.metadata?.clientInfo && (
                        <div className="w-64 flex-shrink-0">
                            <div className="border border-gray-200 rounded-lg p-5 sticky top-6">
                                <h3 className="font-semibold text-gray-900 mb-4">About the client</h3>
                                <div className="space-y-3 text-sm">
                                    {proposal.metadata.clientInfo.paymentVerified && (
                                        <div className="flex items-center gap-2 text-success-600 pb-3 border-b border-gray-100">
                                            <CheckCircle className="h-4 w-4" />
                                            Payment verified
                                        </div>
                                    )}
                                    {proposal.metadata.clientInfo.rating && (
                                        <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                                            <span className="text-yellow-500">★</span>
                                            <span className="font-medium">{proposal.metadata.clientInfo.rating.toFixed(1)}</span>
                                        </div>
                                    )}
                                    {proposal.metadata.clientInfo.country && (
                                        <div className="flex items-center gap-2 text-gray-700 pb-3 border-b border-gray-100">
                                            <MapPin className="h-4 w-4 text-gray-400" />
                                            {proposal.metadata.clientInfo.country}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default InlineProposalDetails;
